import { JSX, Suspense, useMemo } from "react";

import { Grid, Paper, useTheme } from "@mui/material";
import dayjs from "dayjs";
import { useTranslation } from "react-i18next";
import { useSummarizedHistoryChartQuery } from "src/api/locationChartQueries.api";
import CardHeader from "src/components/CardHeader/CardHeader";
import ChartSkeleton from "src/components/ChartSkeleton/ChartSkeleton";
import DownloadCSV from "src/components/DownloadCSV/DownloadCSV";
import { IntervalTab } from "src/components/IntervalTabs/enum/IntervalTab";
import TableView from "src/components/TableView/TableView";
import ComponentId from "src/constants/componentId.enum";
import IntervalType from "src/constants/intervalType.enum";
import MeasureType from "src/constants/measureType.enum";
import sharedStyles from "src/constants/styles";
import { mapFootfallPerEntrance } from "src/lib/chart.helper";
import { getDateTime, getIntervalFromSelectedTab, getMeasuresData, getToday } from "src/lib/date.helper";
import { filterAndSortMeasuresSummarizedDto } from "src/lib/number.helpers";
import useIntervalTabsStore from "src/store/intervalTabs.store";
import { type MeasuresSummarizedDto } from "src/types/dashboardDatasetDto";
import type LocationType from "src/types/LocationType";

const styles = {
  fullWidth: { xs: 12 },
  tableGrid: { height: "100%", width: "100%" },
  intervalTabsContainer: { mt: { xs: 2, md: 1 } },
};
const rows = [12];

function FootfallPerEntrance({
  selectedLocation,
  displayedType,
  componentId,
}: {
  selectedLocation: { Id: string; LocalName: string };
  displayedType: LocationType;
  componentId: string;
}): JSX.Element {
  const { t } = useTranslation();
  const theme = useTheme();
  const { componentStates } = useIntervalTabsStore();

  const componentState = componentStates[componentId] ?? {
    selectedTab: IntervalTab.Day,
    view: undefined,
    selectedDate: undefined,
  };

  const selectedDate = componentState.selectedDate ? dayjs(componentState.selectedDate) : null;

  const { data: measuresDataToday } = useSummarizedHistoryChartQuery(
    [selectedLocation.Id],
    IntervalType.Day,
    selectedDate,
    MeasureType.Count
  );
  const { data: measuresDataWeek } = useSummarizedHistoryChartQuery(
    [selectedLocation.Id],
    IntervalType.Week,
    selectedDate,
    MeasureType.Count
  );
  const { data: measuresDataMonth } = useSummarizedHistoryChartQuery(
    [selectedLocation.Id],
    IntervalType.Month,
    selectedDate,
    MeasureType.Count
  );
  const { data: measuresDataYear } = useSummarizedHistoryChartQuery(
    [selectedLocation.Id],
    IntervalType.IsoYear,
    selectedDate,
    MeasureType.Count
  );

  const measuresData = useMemo(
    () => getMeasuresData(componentState.selectedTab, measuresDataToday, measuresDataWeek, measuresDataMonth, measuresDataYear),
    [componentState.selectedTab, measuresDataToday, measuresDataWeek, measuresDataMonth, measuresDataYear]
  ) as MeasuresSummarizedDto;

  const filteredMeasuresData = useMemo(() => filterAndSortMeasuresSummarizedDto(measuresData), [measuresData]);

  const intervalType = getIntervalFromSelectedTab(componentState.selectedTab);

  const dataRows = useMemo(
    () => (filteredMeasuresData ? mapFootfallPerEntrance(filteredMeasuresData, intervalType, selectedLocation.LocalName) : []),
    [filteredMeasuresData, intervalType, selectedLocation.LocalName]
  );

  return (
    <Paper sx={sharedStyles(theme).cardContainer}>
      <CardHeader
        header={t(`footfall per entrance`)}
        dateTime={getDateTime(measuresData, componentState.selectedTab, selectedDate ?? getToday(), t)}
        children={<DownloadCSV componentId={ComponentId.FootfallPerEntrance} locationName={selectedLocation.LocalName} rows={dataRows} />}
      />
      <Grid container spacing={2} mt={1}>
        <Grid size={styles.fullWidth} sx={styles.tableGrid}>
          <Suspense fallback={<ChartSkeleton />}>
            {filteredMeasuresData ? (
              <TableView measuresData={filteredMeasuresData} rows={rows} displayedType={displayedType} />
            ) : (
              <ChartSkeleton />
            )}
          </Suspense>
        </Grid>
      </Grid>
    </Paper>
  );
}

export default FootfallPerEntrance;
